import React, { useCallback, useEffect, useState } from 'react';
import { fetchKarmaStatus, KarmaStatus, updateKarmaStatus } from '../../api/karma';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface KarmaPanelProps {
  isAdmin: boolean;
  pollIntervalMs?: number;
}

export const KarmaPanel: React.FC<KarmaPanelProps> = ({ isAdmin, pollIntervalMs = 10000 }) => {
  const [karma, setKarma] = useState<KarmaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadKarma = useCallback(
    async (showSpinner = false) => {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        setLoading(false);
        setError('Missing access token. Please log in again.');
        return;
      }

      if (showSpinner) setLoading(true);
      try {
        const data = await fetchKarmaStatus(accessToken);
        setKarma(data);
        setError('');
      } catch {
        setKarma(null);
        setError('Failed to load karma status.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadKarma(true);
    const intervalId = window.setInterval(() => {
      loadKarma();
    }, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadKarma, pollIntervalMs]);

  return (
    <KarmaDisplay
      karma={karma}
      loading={loading}
      error={error}
      isAdmin={isAdmin}
      onUpdated={() => loadKarma(true)}
    />
  );
};

interface KarmaDisplayProps {
  karma: KarmaStatus | null;
  loading: boolean;
  error: string;
  isAdmin: boolean;
  onUpdated: () => void;
}

const KarmaDisplay: React.FC<KarmaDisplayProps> = ({ karma, loading, error, isAdmin, onUpdated }) => {
  const [formState, setFormState] = useState(() => ({
    reward_points: karma?.reward_points ?? 0,
    punishment_points: karma?.punishment_points ?? 0,
    tally: karma?.tally ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!karma) return;
    setFormState({
      reward_points: karma.reward_points,
      punishment_points: karma.punishment_points,
      tally: karma.tally,
    });
  }, [karma]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="h-2 w-24 animate-pulse rounded-full bg-muted" />
        <div className="mt-4 space-y-3">
          <div className="h-2 animate-pulse rounded bg-muted" />
          <div className="h-2 animate-pulse rounded bg-muted" />
          <div className="h-2 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-destructive shadow-sm">
        {error}
      </div>
    );
  }

  if (!karma) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground shadow-sm">
        No karma data available yet.
      </div>
    );
  }

  const totalPoints = Math.max(karma.reward_points + karma.punishment_points, 1);

  const rewardWidth = Math.min(100, (karma.reward_points / totalPoints) * 100);
  const punishmentWidth = Math.min(100, (karma.punishment_points / totalPoints) * 100);

  const tallyMessage =
    karma.tally > 0
      ? `${karma.tally} point${karma.tally === 1 ? '' : 's'} towards a reward`
      : karma.tally < 0
        ? `${Math.abs(karma.tally)} point${Math.abs(karma.tally) === 1 ? '' : 's'} towards a punishment`
        : 'Neutral balance';

  return (
    <div className="space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Reward Points</span>
            <span className="font-semibold text-foreground">{karma.reward_points}</span>
          </div>
          <Progress value={rewardWidth} className="mt-2" />
        </div>

        <div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Punishment Points</span>
            <span className="font-semibold text-foreground">{karma.punishment_points}</span>
          </div>
          <Progress value={punishmentWidth} className="mt-2 bg-muted">
            {/* fallback handled by component */}
          </Progress>
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-border/70 bg-muted/40 p-4 text-sm">
        <span className="font-semibold text-foreground">Tally:</span> {tallyMessage}
      </div>

      {isAdmin && karma.updated_at && (
        <div className="text-xs text-muted-foreground">
          Last updated: {new Date(karma.updated_at).toLocaleString()}
        </div>
      )}

      {isAdmin && (
        <AdminKarmaControls
          formState={formState}
          setFormState={setFormState}
          saving={saving}
          saveError={saveError}
          setSaveError={setSaveError}
          setSaving={setSaving}
          onUpdated={onUpdated}
        />
      )}
    </div>
  );
};

interface AdminKarmaControlsProps {
  formState: {
    reward_points: number;
    punishment_points: number;
    tally: number;
  };
  setFormState: React.Dispatch<React.SetStateAction<{
    reward_points: number;
    punishment_points: number;
    tally: number;
  }>>;
  saving: boolean;
  saveError: string;
  setSaveError: React.Dispatch<React.SetStateAction<string>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdated: () => void;
}

const AdminKarmaControls: React.FC<AdminKarmaControlsProps> = ({
  formState,
  setFormState,
  saving,
  saveError,
  setSaveError,
  setSaving,
  onUpdated,
}) => {
  const handleChange = (field: keyof AdminKarmaControlsProps['formState']) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setFormState(prev => ({ ...prev, [field]: Number.isNaN(value) ? 0 : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return;

    setSaving(true);
    setSaveError('');
    try {
      await updateKarmaStatus(accessToken, {
        reward_points: Math.max(0, formState.reward_points),
        punishment_points: Math.max(0, formState.punishment_points),
        tally: formState.tally,
      });
      onUpdated();
    } catch {
      setSaveError('Failed to update karma. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-border bg-background p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-semibold">Admin Controls</h4>
        {saving && <span className="text-xs text-muted-foreground">Saving…</span>}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="space-y-2 text-sm font-medium text-muted-foreground">
          Reward Points
          <Input
            type="number"
            min={0}
            value={formState.reward_points}
            onChange={handleChange('reward_points')}
          />
        </label>
        <label className="space-y-2 text-sm font-medium text-muted-foreground">
          Punishment Points
          <Input
            type="number"
            min={0}
            value={formState.punishment_points}
            onChange={handleChange('punishment_points')}
          />
        </label>
        <label className="space-y-2 text-sm font-medium text-muted-foreground">
          Tally
          <Input
            type="number"
            value={formState.tally}
            onChange={handleChange('tally')}
          />
        </label>
      </div>
      {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      <Button type="submit" disabled={saving}>
        {saving ? 'Saving…' : 'Update Karma'}
      </Button>
    </form>
  );
};
